<template>
  <div>
    <BaseModal
      :value="isOpen"
      :modal-title="
        !activeCourse
          ? $t('course.createDialogTitle')
          : $t('course.editDialogTitle')
      "
      is-complex
      @click:outside="$emit('close')"
      @keydown.esc="$emit('close')"
      @close="$emit('close')"
    >
      <template #modal>
        <div>
          <BaseTextField
            v-model="name"
            autofocus
            :error-messages="nameErrors"
            :has-error="nameErrors.length > 0"
            :label="`${$t('course.forms.create.nameLabel')}*`"
            color="primary"
          />
          <BaseMarkdownEditor
            v-model="description"
            :label="$t('course.forms.create.descriptionLabel')"
            :placeholder="$t('course.forms.create.descriptionPlaceholder')"
            :with-link="false"
          />
          <BaseMarkdownEditor
            v-model="consentText"
            :label="$t('course.forms.create.consentText')"
            :placeholder="$t('course.forms.create.ConsentTextPlaceholder')"
            :with-link="false"
            class="mt-6"
          />
          <v-switch
            v-model="anonymous"
            :label="$t('course.forms.create.anonymousLabel')"
            inset
            hide-details
            @change="openConfirmAnonymous()"
          />
          <div class="remind-text">
            {{ $t("course.reminderFrequencyLabel") }}
          </div>
          <base-autocomplete
            v-model="reminderFrequencies"
            :items="reminderItems"
            :label="$t('course.reminderFrequency')"
            multiple
            clearable
          />
        </div>
      </template>
      <template #actions>
        <BaseButton
          text
          class="mx-2 ml-auto"
          type="secondary"
          color="#707080"
          @click="
            () => {
              $emit('close');
              clear();
            }
          "
        >
          {{ $t("utils.cancel") }}
        </BaseButton>
        <BaseButton
          color="primary"
          type="primary"
          :loading="submiting"
          :submit="true"
          @click="submit"
        >
          {{ !activeCourse ? $t("course.create") : $t("utils.edit") }}
        </BaseButton>
      </template>
    </BaseModal>
    <CourseConfirmChangeAnonymousStatus
      v-if="activeCourse && displayConfirmAnonymous"
      :course-name="activeCourse.name"
      @confirm="handleConfirmAnonymousChange($event)"
    />
  </div>
</template>

<script>
import CourseConfirmChangeAnonymousStatus from "./CourseConfirmChangeAnonymousStatus.vue";
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
  name: "CourseCreateAndEditDialog",
  components: {
    CourseConfirmChangeAnonymousStatus,
  },
  mixins: [validationMixin],
  props: {
    isOpen: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      displayConfirmAnonymous: false,
      anonymous: false,
      description: "",
      name: "",
      submiting: false,
      consentText: "",
      reminderFrequencies: [],
    };
  },

  validations: {
    name: {
      required,
      maxLength: maxLength(100),
    },
  },
  computed: {
    ...mapState({
      activeCourse: (state) => state.course.course,
    }),
    nameErrors() {
      let errors = "";
      if (!this.$v.name.$dirty) return errors;
      !this.$v.name.required &&
        (errors = this.$t("course.forms.create.nameRequired"));
      !this.$v.name.maxLength &&
        (errors = this.$t("course.forms.create.nameMaxLength", { n: 100 }));
      return errors;
    },
    reminderItems() {
      const value = [];
      for (let i = 0; i < 100; i++) {
        value.push(`${i}`);
      }
      return value;
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.setCourse();
      }
    },
  },
  methods: {
    clear() {
      this.$v.$reset();
      this.name = "";
      this.description = "";
      this.anonymous = false;
      this.consentText = "";
    },
    setCourse() {
      if (this.activeCourse) {
        this.anonymous = this.activeCourse.anonymous;
        this.description = this.activeCourse.description;
        this.name = this.activeCourse.name;
        this.consentText = this.activeCourse.consentText;
        this.reminderFrequencies = this.activeCourse.reminderFrequencies.length
          ? this.activeCourse.reminderFrequencies.split(",")
          : [];
      }
    },
    async submit() {
      this.$v.name.$touch();
      if (this.$v.$invalid) return;
      this.submiting = true;
      if (!this.activeCourse) {
        await this.createCourse();
      } else {
        await this.editCourse();
      }
      this.submiting = false;
      this.$emit("close");
      this.clear();
    },
    async createCourse() {
      try {
        const payload = {
          name: this.name,
          description: this.description,
          anonymous: this.anonymous,
          consent_text: this.consentText,
        };
        if (this.reminderFrequencies.length)
          payload.reminder_frequencies = this.reminderFrequencies.join(",");
        const course = await this.$store.dispatch("course/createCourse", {
          organizationId: this.$route.params.organizationId,
          payload,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("course.createConfirmation"),
          type: "SUCCESS",
        });
        this.$router.push({
          name: "courseDetail",
          params: {
            organizationId: this.$route.params.organizationId,
            courseId: course.id,
          },
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async editCourse() {
      try {
        const payload = {
          name: this.name,
          description: this.description,
          anonymous: this.anonymous,
          consent_text: this.consentText,
        };
        if (this.reminderFrequencies.length)
          payload.reminder_frequencies = this.reminderFrequencies.join(",");
        await this.$store.dispatch("course/editCourse", {
          organizationId: this.$route.params.organizationId,
          courseId: this.$route.params.courseId,
          payload,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("course.editConfirmation"),
          type: "SUCCESS",
        });
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isEditingCourse = false;
    },
    openConfirmAnonymous() {
      if (!this.activeCourse) return;
      this.displayConfirmAnonymous = !this.anonymous;
    },
    handleConfirmAnonymousChange(value) {
      this.anonymous = !value;
      this.displayConfirmAnonymous = false;
    },
  },
};
</script>
<style scoped>
.remind-text {
  margin: 16px 0 4px 0;
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 15px;
  color: #707080;
}
</style>
